import { useMemo } from 'react';

import type {
  CatalogProviderLoaderResult,
  CatalogProviderSummary,
  NormalizedCatalogSearchFilters
} from '@/features/catalog';

import { hasMorePages } from '../services/formatters';
import type { UsePuppySearchResult } from '../usePuppySearch';

type UsePuppySearchResultsArgs = {
  providers: CatalogProviderSummary[];
  pagination: CatalogProviderLoaderResult['pagination'];
  status: UsePuppySearchResult['status'];
  filters: NormalizedCatalogSearchFilters;
};

type UsePuppySearchResults = {
  hasResults: boolean;
  showLoadMore: boolean;
  progressLabel: string;
  pageLabel: string | null;
  status: UsePuppySearchResult['status'];
  filters: NormalizedCatalogSearchFilters;
};

export function usePuppySearchResults({
  providers,
  pagination,
  status,
  filters
}: UsePuppySearchResultsArgs): UsePuppySearchResults {
  const hasResults = providers.length > 0;

  const loadedCount = useMemo(
    () =>
      providers.reduce(
        (count, provider) =>
          count + provider.listings.length + provider.waitlists.length,
        0
      ),
    [providers]
  );

  const showLoadMore = useMemo(
    () => hasResults && hasMorePages(pagination),
    [hasResults, pagination]
  );

  const totalCount = Math.max(pagination.totalItems, loadedCount);

  const progressLabel = showLoadMore
    ? `Showing ${loadedCount.toLocaleString()} of ${totalCount.toLocaleString()}`
    : `Showing all ${totalCount.toLocaleString()}`;

  const pageLabel =
    pagination.totalPages > 1
      ? `Page ${pagination.page.toLocaleString()} of ${pagination.totalPages.toLocaleString()}`
      : null;

  return {
    hasResults,
    showLoadMore,
    progressLabel,
    pageLabel,
    status,
    filters
  };
}
